import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import type * as Y from 'yjs';
import type { Checkin, CheckinPhotoRef, CheckinPlace } from '@awwyeah/shared';
import { CHECKINS_DOC, CHECKINS_MAP_KEY, shouldStampSeen } from '@awwyeah/shared';
import { useYDoc, useYMap } from '../../sync';
import { useUser } from '../auth';
import type { PreparedPhoto } from './outbox/image';
import { localPhotoRef } from './outbox/image';
import {
  getCheckinOutbox,
  onPhotoUploaded,
  onPhotoUploading,
  pendingPhotosSnapshot,
  subscribeToOutbox,
} from './outbox';
import { sortNewestFirst } from './feed';

/**
 * The module's data layer: one Yjs map, plus the outbox that carries the photos.
 *
 * Every piece of check-in *metadata* — the post, its text, its place, its seen-by stamps,
 * and the photo's upload state — is a Yjs write (ROADMAP §1.3). Nothing here ever POSTs a
 * post. That is what makes an offline check-in indistinguishable from an online one to the
 * person making it: `map.set()` is local, instant and replayed by the sync layer whenever
 * the network comes back.
 */

export interface PostInput {
  text?: string;
  place?: CheckinPlace;
  photo?: PreparedPhoto;
}

export interface CheckinsApi {
  checkins: Checkin[];
  post: (input: PostInput) => Promise<string>;
  /** Stamp `seenBy` for the signed-in member. Idempotent; ignores your own posts. */
  markSeen: (checkinId: string) => void;
  selfId: string | null;
}

function newId(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 20);
}

export function useCheckins(): CheckinsApi {
  const { user } = useUser();
  const doc = useYDoc(CHECKINS_DOC);
  const { map, entries } = useYMap<Checkin>(doc, CHECKINS_MAP_KEY);

  const checkins = useMemo(
    () => sortNewestFirst(entries.map(([, checkin]) => checkin).filter(Boolean)),
    [entries],
  );

  useUploadStateSync(map);

  const post = useCallback(
    async (input: PostInput): Promise<string> => {
      if (!user) throw new Error('not signed in');
      const id = newId();
      const photoRef: CheckinPhotoRef | undefined = input.photo
        ? localPhotoRef(input.photo)
        : undefined;

      const checkin: Checkin = {
        id,
        authorId: user.id,
        createdAt: new Date().toISOString(),
        ...(input.text ? { text: input.text } : {}),
        ...(input.place ? { place: input.place } : {}),
        ...(photoRef ? { photoRef } : {}),
        seenBy: {},
      };

      // The post itself lands first and instantly — text and place sync before photos.
      map.set(id, checkin);

      const outbox = getCheckinOutbox();
      if (input.photo && photoRef) {
        await outbox.queuePhoto({
          id: photoRef.id,
          checkinId: id,
          blob: input.photo.blob,
          contentType: input.photo.contentType,
          width: photoRef.w,
          height: photoRef.h,
        });
      }
      await outbox.queueAnnouncement({
        checkinId: id,
        ...(input.text ? { text: input.text } : {}),
        ...(input.place ? { placeName: input.place.name } : {}),
        ...(photoRef ? { photoId: photoRef.id } : {}),
      });

      return id;
    },
    [map, user],
  );

  const markSeen = useCallback(
    (checkinId: string) => {
      if (!user) return;
      const checkin = map.get(checkinId);
      if (!checkin) return;
      if (!shouldStampSeen(checkin, user.id)) return;
      map.set(checkinId, {
        ...checkin,
        seenBy: { ...checkin.seenBy, [user.id]: new Date().toISOString() },
      });
    },
    [map, user],
  );

  return { checkins, post, markSeen, selfId: user?.id ?? null };
}

/**
 * Mirrors the outbox's progress into Yjs: `local → uploading → uploaded`. Only the
 * author's own device runs this (only it has the bytes), and only forwards, so the flip is
 * a conflict-free last-writer-wins on a field nobody else touches.
 */
function useUploadStateSync(map: Y.Map<Checkin>): void {
  const mapRef = useRef(map);
  mapRef.current = map;

  useEffect(() => {
    const setState = (
      checkinId: string,
      photoId: string,
      state: CheckinPhotoRef['state'],
    ): void => {
      const checkin = mapRef.current.get(checkinId);
      if (!checkin?.photoRef || checkin.photoRef.id !== photoId) return;
      if (checkin.photoRef.state === state) return;
      // `uploaded` is terminal — never walk a confirmed photo backwards.
      if (checkin.photoRef.state === 'uploaded') return;
      mapRef.current.set(checkinId, { ...checkin, photoRef: { ...checkin.photoRef, state } });
    };

    const offUploading = onPhotoUploading((record) =>
      setState(record.checkinId, record.id, 'uploading'),
    );
    const offUploaded = onPhotoUploaded((record) =>
      setState(record.checkinId, record.id, 'uploaded'),
    );
    return () => {
      offUploading();
      offUploaded();
    };
  }, []);
}

/** Photos still sitting on this device — the feed marks these and renders the local blob. */
export function usePendingPhotos(): ReturnType<typeof pendingPhotosSnapshot> {
  useEffect(() => {
    getCheckinOutbox();
  }, []);
  return useSyncExternalStore(subscribeToOutbox, pendingPhotosSnapshot, pendingPhotosSnapshot);
}
